// Translates the Mini-Profile form's react-hook-form output (nested under
// generalInformation/personalInformation/affordableInformation, matching
// useMiniProfileSections's field names) into the exact payload shape each
// backend endpoint expects. The two shapes diverge in several places — this
// is NOT a pass-through. Traced against fortress-backend directly (line refs
// below), not just field names, since a wrong id or key here silently
// updates the wrong record or drops a field instead of erroring.
//
// Pending Applicants are saved via Applicants.js#updateApplicant; only a
// subset of General Information fields are persisted (see
// buildPendingApplicantSavePayload below).

import {
  DEFAULT_PHONE_TYPE,
  DEFAULT_EMAIL_TYPE,
  getAdditionalContactEntries,
} from './additionalContactFields';
import { getMainEmailAndPhone } from '../../../components/EditHouseholdForm/utils';

// resident.schema.js/minor.js's MiniProfileData Joi schemas are inconsistent
// about blank values: some string fields are Joi.string().optional().allow(null)
// (null OK, '' is NOT), others — race/gender/ethnicity/veteran/jointCustody/
// studentType — are bare Joi.string() with neither .allow(null) NOR
// .allow(''). react-hook-form's defaultValues use '' for "no value" (see
// useMiniProfileSections's getDefaultValues), so sending an untouched blank
// field as-is 400s ("Invalid request payload input", no field-level detail
// — found by replaying a real captured Save request against the local
// backend, 2026-07-28). The one shape every optional field accepts
// regardless of its particular Joi rule is an *omitted* key — both command
// handlers only touch a column `if (data.someField !== undefined)`, so
// omitting also means "leave this column alone", which is what an untouched
// blank field should do anyway.
// Only plain object literals should be walked field-by-field. startDate/
// endDate arrive here as live Moment instances (the date field variant's
// onChange value) — recursing into one via Object.entries/fromEntries
// strips its prototype (losing toJSON/toISOString) and collapses its
// internal Date (_d) down to `{}` (Date has no own enumerable properties),
// silently corrupting the value into unparseable garbage instead of the
// ISO string JSON.stringify would otherwise produce via Moment's own
// toJSON. Found by replaying a real captured Save request (2026-07-29).
const isPlainObject = (value) => value.constructor === Object;

// The currency field's raw value can be a comma/dollar-sign-formatted
// string — resident.schema.js's annualIncome is Joi.number(), so this has to
// resolve to a real number before it's sent. Same parsing as
// getAnnualIncomeValidate (fieldRules.js) — not utils/redux-form-helper's
// sanitizeNumberInput, which caps at 7 integer digits (built for a
// different, lower-ceiling field) well under this one's real max.
const toAnnualIncome = (value) => {
  if (value === '' || value === null || value === undefined) return undefined;
  const num = Number(String(value).replace(/[^0-9.]/g, ''));
  return Number.isFinite(num) ? num : undefined;
};

// toAnnualIncome maps blank to undefined, which JSON.stringify drops -- the
// backend then reads "leave this column alone" and never clears it. Same
// treatment parentCompany already gets.
//
// annualIncome is Applicant-only, with no Resident-side fallback the way
// personalInformation's other fields get one from parseResidentInfo's
// fallbackCustomer -- so for a Prior member, whose Applicant row never
// loads (nullIfBlankLoaded's comment below, same root cause), this shows
// blank exactly like a genuinely cleared value. loadedValue is the same
// occupantInfo.residentInfo comparison nullIfBlankLoaded makes: null only
// when the loaded record actually had something to clear, else omitted.
// loadedValue is required, not defaulted -- optional-chaining into a real
// occupantInfo already produces `undefined` for "never loaded", so a default
// parameter (which also fires on an explicit `undefined` argument) couldn't
// tell that apart from a caller that meant "always clear." A caller with no
// Prior-member problem to guard against (buildPendingApplicantSavePayload's
// Applicant record always loads reliably while Pending) passes `true`.
const toClearableAnnualIncome = (value, loadedValue) => {
  const parsed = toAnnualIncome(value);
  return parsed !== undefined ? parsed : loadedValue ? null : undefined;
};

// A nested object whose fields are ALL blank (e.g. disability's 5 sub-fields
// each untouched) still collapses to a plain, truthy `{}` after the leaf
// values below become undefined — which is indistinguishable from "the user
// explicitly set this" to a bare `if (payload.disability)` check on the
// backend. ApplicantMinors.js#updateMinor has exactly that check, and
// unconditionally does `findByPk(applicantMinorId)` when it's truthy; for a
// Prior member with no real ApplicantMinor to look up (see
// useHouseholdMembersQuery.helpers.js's residentId fallback), that throws
// and 500s the whole save — even though every other field saved
// successfully. Collapsing an all-undefined object to undefined itself
// means the key is omitted from the JSON body entirely, matching what an
// untouched top-level section already does. Found by replaying a real
// captured Save request for a Prior minor (2026-07-29).
const blankToUndefined = (value) => {
  if (value === '') return undefined;
  if (
    value == null ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    !isPlainObject(value)
  ) {
    return value;
  }
  const entries = Object.entries(value).map(([key, v]) => [
    key,
    blankToUndefined(v),
  ]);
  if (entries.length > 0 && entries.every(([, v]) => v === undefined)) {
    return undefined;
  }
  return Object.fromEntries(entries);
};

// startDate/endDate are the one exception: Joi.date().optional().allow(null)
// explicitly supports null, and unlike "leave this column alone", clearing a
// previously-set End Date back to blank is a real, meaningful edit that must
// reach the backend as an explicit null — omitting the key here (the default
// blankToUndefined behavior) would mean it's silently never cleared.
const withNullableDates = (generalInformation) => ({
  ...generalInformation,
  startDate:
    generalInformation.startDate === '' ? null : generalInformation.startDate,
  endDate:
    generalInformation.endDate === '' ? null : generalInformation.endDate,
});

// Residents.js#updateAdultResident (called from #updateResident) reads
// data.type as a fallback-free source for the Resident's own
// applicantTypeId column — but resident.schema.js's MiniProfileData never
// declares a `type` key under generalInformation, only `applicantTypeId`
// (Joi rejects unknown keys by default, another 400 found via the same
// replay). Sending applicantTypeId (matching the schema) still works
// end-to-end: updateAdultResident's *Applicant*-record write already falls
// back to `data.type || data.applicantTypeId`, and the Resident's own
// applicantTypeId column is independently covered by the applicantInformation
// block below (Residents.js#updateResident's separate, dedicated write).

// relationshipId defaults to the literal string "default" (a frontend-only
// placeholder — useHouseholdMembersQuery.helpers.js's parseGeneralInfo hands
// it to ANY member with no relationship on file, not just the primary/
// head-of-household one) whenever there's nothing saved to echo back.
// updateAdultResident normally nulls this out itself
// (`if (data.isPrimary) residentUpdateInfo.relationshipId = null`), but only
// when the payload includes `isPrimary` — a field MiniProfileData doesn't
// even declare. Sending "default" straight through hits residentsRepo.update
// as an invalid relationshipId (not a real FK/uuid), which rejects — and
// because that call sits inside a Promise.all wrapped in a .catch that
// returns a plain object instead of an array, the destructuring on the
// other end throws a confusing "(intermediate value) is not iterable"
// instead of a real error message. Found by replaying a real Save request
// against the local backend for a primary/head-of-household member
// (2026-07-28).
const withoutDefaultRelationshipId = (relationshipId) =>
  relationshipId === 'default' ? null : relationshipId;

// 'default' is not an ApplicantType uuid; omitted (not nulled) to keep the saved value.
const withoutDefaultApplicantTypeId = (applicantTypeId) =>
  ['', 'default'].includes(applicantTypeId) ? undefined : applicantTypeId;

// Any select field whose backend column both (a) allows null and (b) is
// written unconditionally (no `!== undefined`/isNil guard) has the same
// startDate/endDate-style problem: blankToUndefined's default (omit the key)
// leaves a previously-saved value untouched instead of actually clearing it,
// since an omitted key means "leave this column alone." 'default' is
// App/selectors.js's getStateOptions/getCountryOptions blank-placeholder
// value (see withoutDefaultRelationshipId above for the same quirk on
// relationshipId) — the Mini-Profile's own blank convention elsewhere is '',
// so both need to be treated as "clear this".
const nullIfBlank = (value) => (['', 'default'].includes(value) ? null : value);

// multiRace is only in the form where the multi-select replaces the single
// Race field; elsewhere it stays out of the request. Where it is shown, the
// stored `race` is no longer editable but the TIC, Demographic report and
// application PDF still read it, so it mirrors the first selection (as the
// backend's buildRacePayload does) instead of going stale.
const toRacePayload = ({ race, multiRace }) => {
  // Key left out entirely, not set to undefined: buildCreateMinorPayload
  // runs this through removeDefaultsToNull, which would turn it into null.
  if (!Array.isArray(multiRace)) return { race };
  const selectedRaces = multiRace.filter(Boolean);
  return selectedRaces.length > 0
    ? { race: selectedRaces[0], multiRace: selectedRaces }
    : { race: null, multiRace: null };
};

// Prior commercial tenant: applicantInfo (the source of these 5 columns)
// never loads, so a blank here can mean "untouched" or "genuine clear" --
// null only when the loaded record had something to clear, else omit.
const nullIfBlankLoaded = (value, loadedValue) => {
  if (!['', 'default'].includes(value)) return value;
  return loadedValue ? null : undefined;
};

// stateIdOrigin/passportCountry/maritalStatus/stateIdNumber/passportNumber/
// alienRegistration: resident.schema.js declares Joi.string().optional().
// allow(null) for all six, written via customersRepo.updateByResidentId
// with no per-field guard — clearing a saved State ID #, Passport #, or
// Alien Registration (e.g. swapping to a different identification type)
// silently no-opped without this, the same "omitted key" problem as the
// other three.
const withNullableIdFields = (personalInformation) => ({
  ...personalInformation,
  maritalStatus: nullIfBlank(personalInformation.maritalStatus),
  stateIdOrigin: nullIfBlank(personalInformation.stateIdOrigin),
  passportCountry: nullIfBlank(personalInformation.passportCountry),
  stateIdNumber: nullIfBlank(personalInformation.stateIdNumber),
  passportNumber: nullIfBlank(personalInformation.passportNumber),
  alienRegistration: nullIfBlank(personalInformation.alienRegistration),
});

// gender/race/ethnicity/veteran (adult) and gender/race/ethnicity/
// jointCustody/studentType (minor) are written the same unconditional way
// (Residents.js#updateResident's customersRepo.updateByResidentId /
// ApplicantMinors.js#updateMinor's this.repository.update); their Joi
// schemas were relaxed to .allow(null) alongside this fix.
// affordableRelationshipId is written behind a hand-rolled filter in both
// handlers — Residents.js now checks `!== undefined` (was isNil, which
// treated an explicit null the same as an omitted key) and
// ApplicantMinors.js now lets `=== null` through alongside its existing
// truthy+uuid check. Only touches keys actually present, since adult/minor
// payloads don't share the exact same field set.
const NULLABLE_AFFORDABLE_KEYS = [
  'gender',
  'veteran',
  'jointCustody',
  'race',
  'ethnicity',
  'studentType',
  'affordableRelationshipId',
];
// customerDisabilitiesRepo#createOrUpdateDisabilityByCustomerId writes this
// nested object the same unconditional way — resident.schema.js allows null
// for all five — so clearing just one sub-answer (while a sibling still has
// a value, keeping the object from collapsing to undefined entirely) has
// the same omitted-key problem as the top-level keys above.
const NULLABLE_DISABILITY_KEYS = [
  'hasDisability',
  'disabilityMobile',
  'disabilityHearing',
  'disabilityVisual',
  'disabilityOther',
];
const withNullableAffordableFields = (affordableInformation) => {
  const result = { ...affordableInformation };
  NULLABLE_AFFORDABLE_KEYS.forEach((key) => {
    if (key in result) {
      result[key] = nullIfBlank(result[key]);
    }
  });
  // Only normalize when at least one sub-field survives with a real value —
  // leaving an all-blank object's fields as '' lets blankToUndefined's own
  // recursion collapse the whole key to undefined further down, same as an
  // untouched field. Pre-nulling every key here instead would make it
  // survive as an all-null object, which is exactly the "truthy but
  // meaningless" object the comment above buildMinorSavePayload's
  // ApplicantMinors.js#updateMinor callout warns about (500s a Prior member
  // with no real ApplicantMinor to look up).
  const hasRealDisabilityValue = NULLABLE_DISABILITY_KEYS.some(
    (key) =>
      !['', 'default', undefined, null].includes(result.disability?.[key]),
  );
  if (result.disability && hasRealDisabilityValue) {
    const disability = { ...result.disability };
    NULLABLE_DISABILITY_KEYS.forEach((key) => {
      if (key in disability) {
        disability[key] = nullIfBlank(disability[key]);
      }
    });
    result.disability = disability;
  }
  return result;
};

// ENG-30290 -- turns the two flat Secondary/Tertiary form slots (e.g.
// generalInformation.secondaryPhone/.tertiaryPhone) into the
// `additionalPhones`/`additionalEmails` full-array-replace shape the API
// contract expects (CONTEXT.md's "API payload shape decision"). A slot with
// a value but no Type defaults to DEFAULT_PHONE_TYPE/DEFAULT_EMAIL_TYPE (the
// chosen value-vs-Type consistency rule, Task 4); a slot with a Type but no
// value is dropped entirely -- a Type alone isn't a real entry, so it's never
// sent. `id` is deliberately never included: CustomerContactProcessor.
// processPhones/processEmails always fully deletes and recreates every row
// regardless of what id (if any) is sent, so the backend already ignores it.
const buildAdditionalContactEntries = (
  slots,
  { valueKey, typeKey, defaultType },
) =>
  slots
    .filter((slot) => slot?.[valueKey])
    .map((slot) => ({
      [valueKey]: slot[valueKey],
      [typeKey]: slot[typeKey] || defaultType,
    }));

// Omits the `additionalPhones`/`additionalEmails` key entirely (blankTo
// Undefined's "leave this column alone" convention, extended to an array
// since blankToUndefined itself never recurses into or collapses arrays) only
// when there is truly nothing to say: no current entries in either slot AND
// none were loaded on the record to begin with. Once either side has ever
// had a value, the key is always included from then on -- possibly as `[]`,
// the full-array-replace signal that deletes every remaining additional
// entry (see AC#4 / the "Removal must actually delete" task). Also the flag
// gate (D2): OFF means these fields never rendered, so nothing should ever
// be sent for them regardless of what's sitting in a loaded record.
const withAdditionalContactArray = (
  currentEntries,
  loadedEntries,
  isEditHouseholdMemberDatesEnabled,
) => {
  if (!isEditHouseholdMemberDatesEnabled) return undefined;
  if (currentEntries.length === 0 && loadedEntries.length === 0) {
    return undefined;
  }
  return currentEntries;
};

// The CREATE path's contact shape, deliberately different from the update
// paths' additionalPhones/additionalEmails above. The Add flow saves through
// the legacy PATCH .../residents/{residentId}/household endpoint
// (buildCreateAdultPayload below), whose validator/handler expect the FULL
// set of ContactEmail/ContactPhone rows -- primary included, each entry
// carrying its own `isPrimary` -- not an additional-only array. Reuses
// buildAdditionalContactEntries so the value-vs-Type consistency rule (a
// Type alone with no value is dropped; a value with no Type defaults) stays
// defined in exactly one place -- the primary entry gets the same
// defaulting, matching the application form's own Joi `.default()` on
// phoneType/emailType (never required, always defaulted).
const buildCreateContactArray = (
  primaryValue,
  primaryType,
  additionalSlots,
  { valueKey, typeKey, defaultType },
) => {
  const additionalEntries = buildAdditionalContactEntries(additionalSlots, {
    valueKey,
    typeKey,
    defaultType,
  }).map((entry) => ({ ...entry, isPrimary: false }));

  // Primary entries are capped at 1 and additional slots are always exactly
  // 2 fixed fields (Secondary/Tertiary), so this array can never exceed the
  // backend's max(3) -- no separate slice/guard needed.
  return primaryValue
    ? [
        {
          [valueKey]: primaryValue,
          [typeKey]: primaryType || defaultType,
          isPrimary: true,
        },
        ...additionalEntries,
      ]
    : additionalEntries;
};

// Assembles `data.contactInformation` for buildCreateAdultPayload.
// The primary phone/email's type (design gap, ENG-30373) is NOT gated by
// `edit-household-member-dates` -- that flag only governs the Secondary/
// Tertiary slots (D2) -- so each additional-slots array is built
// conditionally on the flag, while the primary always flows through.
// Returns undefined (key omitted entirely) when there is neither a primary
// nor any additional value for either kind -- an empty `{ emails: [] }`/
// `{ phones: [] }` is never sent, matching the update paths' same
// "nothing to say" omission rule.
const buildCreateAdultContactInformation = (
  generalInformation,
  isEditHouseholdMemberDatesEnabled,
) => {
  const emails = buildCreateContactArray(
    generalInformation.emailAddress,
    generalInformation.emailType,
    isEditHouseholdMemberDatesEnabled
      ? [generalInformation.secondaryEmail, generalInformation.tertiaryEmail]
      : [],
    {
      valueKey: 'emailAddress',
      typeKey: 'emailType',
      defaultType: DEFAULT_EMAIL_TYPE,
    },
  );
  const phones = buildCreateContactArray(
    generalInformation.phoneNumber,
    generalInformation.phoneType,
    isEditHouseholdMemberDatesEnabled
      ? [generalInformation.secondaryPhone, generalInformation.tertiaryPhone]
      : [],
    {
      valueKey: 'phoneNumber',
      typeKey: 'phoneType',
      defaultType: DEFAULT_PHONE_TYPE,
    },
  );

  if (emails.length === 0 && phones.length === 0) return undefined;

  return {
    ...(emails.length > 0 ? { emails } : {}),
    ...(phones.length > 0 ? { phones } : {}),
  };
};

export const buildAdultSavePayload = (
  occupantInfo,
  formValues,
  { isEditHouseholdMemberDatesEnabled = false } = {},
) => {
  const generalInformation = formValues?.generalInformation ?? {};
  const personalInformation = formValues?.personalInformation ?? {};
  const affordableInformation = formValues?.affordableInformation ?? {};

  const loadedAdditionalPhones = getAdditionalContactEntries(
    occupantInfo?.contactInformation?.phones,
  );
  const loadedAdditionalEmails = getAdditionalContactEntries(
    occupantInfo?.contactInformation?.emails,
  );
  const additionalPhones = withAdditionalContactArray(
    buildAdditionalContactEntries(
      [generalInformation.secondaryPhone, generalInformation.tertiaryPhone],
      {
        valueKey: 'phoneNumber',
        typeKey: 'phoneType',
        defaultType: DEFAULT_PHONE_TYPE,
      },
    ),
    loadedAdditionalPhones,
    isEditHouseholdMemberDatesEnabled,
  );
  const additionalEmails = withAdditionalContactArray(
    buildAdditionalContactEntries(
      [generalInformation.secondaryEmail, generalInformation.tertiaryEmail],
      {
        valueKey: 'emailAddress',
        typeKey: 'emailType',
        defaultType: DEFAULT_EMAIL_TYPE,
      },
    ),
    loadedAdditionalEmails,
    isEditHouseholdMemberDatesEnabled,
  );

  return {
    // Resident.id — Residents.js#updateResident reads this from the URL
    // (cmd.residentId), the frontend service also uses it as the URL id.
    id: occupantInfo?.id,
    isAdult: true,
    generalInformation: blankToUndefined(
      withNullableDates({
        preferredName: generalInformation.preferredName,
        firstName: generalInformation.firstName,
        middleName: generalInformation.middleName,
        lastName: generalInformation.lastName,
        // ApplicantMinors.js#updateMinor's own comment spells this out
        // directly: "explicit null clears the suffix" — Residents.js#
        // updateResident writes it the same unconditional way.
        suffixId: nullIfBlank(generalInformation.suffixId),
        phoneNumber: generalInformation.phoneNumber,
        emailAddress: generalInformation.emailAddress,
        applicantTypeId: withoutDefaultApplicantTypeId(
          generalInformation.applicantTypeId,
        ),
        relationshipId: withoutDefaultRelationshipId(
          generalInformation.relationshipId,
        ),
        startDate: generalInformation.startDate,
        endDate: generalInformation.endDate,
        // ENG-30290 -- see buildAdditionalContactEntries/withAdditional
        // ContactArray above. Arrays pass through blankToUndefined/
        // withNullableDates untouched (neither recurses into or normalizes
        // one), so `undefined` here really does omit the key once
        // JSON.stringify drops it, same as every other optional field above.
        additionalPhones,
        additionalEmails,
      }),
    ),
    // Top-level, NOT nested under generalInformation: Residents.js#
    // updateAdultResident reads `data.contactInformation` and passes it
    // straight to CustomerContactProcessor#processContactInformation --
    // full delete-and-recreate of every ContactPhone/ContactEmail row for
    // this contact, so sending exactly one entry per kind (the primary) is
    // correct; there is no secondary/tertiary here to preserve. Type
    // defaults to DEFAULT_PHONE_TYPE/DEFAULT_EMAIL_TYPE when left blank,
    // matching Secondary/Tertiary and the application form's own behavior
    // (Type is never required, always defaulted).
    contactInformation: {
      phones: generalInformation.phoneNumber
        ? [
            {
              phoneNumber: generalInformation.phoneNumber,
              phoneType: generalInformation.phoneType || DEFAULT_PHONE_TYPE,
              isPrimary: true,
            },
          ]
        : undefined,
      emails: generalInformation.emailAddress
        ? [
            {
              emailAddress: generalInformation.emailAddress,
              emailType: generalInformation.emailType || DEFAULT_EMAIL_TYPE,
              isPrimary: true,
            },
          ]
        : undefined,
    },
    personalInformation: blankToUndefined(
      withNullableIdFields({
        birthday: personalInformation.birthday,
        alienRegistration: personalInformation.alienRegistration,
        passportNumber: personalInformation.passportNumber,
        passportCountry: personalInformation.passportCountry,
        stateIdNumber: personalInformation.stateIdNumber,
        stateIdOrigin: personalInformation.stateIdOrigin,
        socialSecurityNumber: personalInformation.socialSecurityNumber,
        maritalStatus: personalInformation.maritalStatus,
      }),
    ),
    affordableInformation: blankToUndefined(
      withNullableAffordableFields({
        gender: affordableInformation.gender,
        veteran: affordableInformation.veteran,
        ...toRacePayload(affordableInformation),
        ethnicity: affordableInformation.ethnicity,
        disability: affordableInformation.disability,
        affordableRelationshipId:
          affordableInformation.affordableRelationshipId,
        // Residents.js#updateResident writes this straight onto Customer.handicapped
        // (resident.schema.js: Joi.boolean()) — nothing server-side derives it from
        // the disability object, so it has to be sent explicitly here, same as the
        // legacy EditOccupantModal's handleEditOccupantSubmit (yesNoToBool) did.
        // Omitted entirely (not `false`) while "Disabled" is unanswered, so an
        // untouched field doesn't get saved as a false "not disabled" answer.
        ...(affordableInformation.disability?.hasDisability
          ? {
              handicapped:
                affordableInformation.disability.hasDisability === 'yes',
            }
          : {}),
      }),
    ),
    // Residents.js#updateResident's separate applicantInformation block is
    // the only path that writes Resident.applicantId — applicantTypeId is
    // redundant with the Applicant-record write above (same value, written
    // via two call sites that happen to target different records).
    // The household query stands in the Resident id for an unreachable
    // Applicant; as Resident.applicantId it violates the FK.
    applicantInformation: {
      applicantId:
        occupantInfo?.applicantId === occupantInfo?.id
          ? undefined
          : occupantInfo?.applicantId,
      applicantTypeId: withoutDefaultApplicantTypeId(
        generalInformation.applicantTypeId,
      ),
    },
    // Top-level per Residents.js#updateResident, NOT nested under
    // affordableInformation like the form has it.
    specialNeedsDesignationIds:
      affordableInformation.specialNeedsDesignationIds,
    // Top-level per resident.schema.js's MiniProfileData, written onto the
    // Applicant record. Omitted key leaves the column untouched; explicit
    // null (via toClearableAnnualIncome) clears it.
    annualIncome: toClearableAnnualIncome(
      personalInformation.annualIncome,
      occupantInfo?.residentInfo?.personalInformation?.annualIncome,
    ),
  };
};

// Different key set entirely from the adult payload (no personalInformation/
// affordableInformation/applicantInformation/specialNeedsDesignationIds), so
// built standalone rather than branched into buildAdultSavePayload.
export const buildCommercialSavePayload = (
  occupantInfo,
  formValues,
  { isEditHouseholdMemberDatesEnabled = false } = {},
) => {
  const generalInformation = formValues?.generalInformation ?? {};
  const personalInformation = formValues?.personalInformation ?? {};
  // The loaded record's own values (useMiniProfileSections.js's
  // getDefaultValues reads the same path) -- absent entirely for a Prior
  // member, since applicantInfo never loads for one. See nullIfBlankLoaded.
  const loadedGeneralInformation =
    occupantInfo?.residentInfo?.generalInformation ?? {};

  // Required, not optional: omitting these makes updateAdultResident skip its
  // reassembly step, and CustomerContactProcessor's delete-and-recreate wipes
  // existing secondary/tertiary rows -- even though this form renders none.
  const loadedAdditionalPhones = getAdditionalContactEntries(
    occupantInfo?.contactInformation?.phones,
  );
  const loadedAdditionalEmails = getAdditionalContactEntries(
    occupantInfo?.contactInformation?.emails,
  );
  const additionalPhones = withAdditionalContactArray(
    buildAdditionalContactEntries(
      [generalInformation.secondaryPhone, generalInformation.tertiaryPhone],
      {
        valueKey: 'phoneNumber',
        typeKey: 'phoneType',
        defaultType: DEFAULT_PHONE_TYPE,
      },
    ),
    loadedAdditionalPhones,
    isEditHouseholdMemberDatesEnabled,
  );
  const additionalEmails = withAdditionalContactArray(
    buildAdditionalContactEntries(
      [generalInformation.secondaryEmail, generalInformation.tertiaryEmail],
      {
        valueKey: 'emailAddress',
        typeKey: 'emailType',
        defaultType: DEFAULT_EMAIL_TYPE,
      },
    ),
    loadedAdditionalEmails,
    isEditHouseholdMemberDatesEnabled,
  );

  return {
    // Resident.id -- Residents.js#updateResident reads this from the URL
    // (cmd.residentId); the frontend service uses it as the URL id too.
    id: occupantInfo?.id,
    isAdult: true,
    // Parent Company/preferredName load from rc.customer (always present), so
    // a plain null-on-blank is safe. The 5 address fields load from
    // applicantInfo (absent for Prior), so they need nullIfBlankLoaded instead.
    generalInformation: blankToUndefined({
      preferredName: nullIfBlank(generalInformation.preferredName),
      firstName: generalInformation.firstName,
      phoneNumber: generalInformation.phoneNumber,
      emailAddress: generalInformation.emailAddress,
      parentCompany: nullIfBlank(generalInformation.parentCompany),
      street: nullIfBlankLoaded(
        generalInformation.street,
        loadedGeneralInformation.street,
      ),
      street2: nullIfBlankLoaded(
        generalInformation.street2,
        loadedGeneralInformation.street2,
      ),
      city: nullIfBlankLoaded(
        generalInformation.city,
        loadedGeneralInformation.city,
      ),
      state: nullIfBlankLoaded(
        generalInformation.state,
        loadedGeneralInformation.state,
      ),
      zipCode: nullIfBlankLoaded(
        generalInformation.zipCode,
        loadedGeneralInformation.zipCode,
      ),
      additionalPhones,
      additionalEmails,
    }),
    // Deliberate copy of buildAdultSavePayload's block, not a shared helper --
    // keeping that function untouched outweighs the duplication. Type is never
    // rendered here; it round-trips via getDefaultValues so it's not clobbered.
    contactInformation: {
      phones: generalInformation.phoneNumber
        ? [
            {
              phoneNumber: generalInformation.phoneNumber,
              phoneType: generalInformation.phoneType || DEFAULT_PHONE_TYPE,
              isPrimary: true,
            },
          ]
        : undefined,
      emails: generalInformation.emailAddress
        ? [
            {
              emailAddress: generalInformation.emailAddress,
              emailType: generalInformation.emailType || DEFAULT_EMAIL_TYPE,
              isPrimary: true,
            },
          ]
        : undefined,
    },
    // Written onto the Applicant record. A blanked value here must clear the
    // column, so this uses the clearable variant, not the shared toAnnualIncome.
    annualIncome: toClearableAnnualIncome(
      personalInformation.annualIncome,
      occupantInfo?.residentInfo?.personalInformation?.annualIncome,
    ),
  };
};

// The applicant-subject twin of buildCommercialSavePayload. Same fields, but shaped
// for applicant.schema.js: strict Joi, so no top-level contactInformation -- phone/email
// types ride inside generalInformation, as buildPendingApplicantSavePayload already does.
export const buildCommercialApplicantSavePayload = (
  occupantInfo,
  formValues,
  { isEditHouseholdMemberDatesEnabled = false } = {},
) => {
  const generalInformation = formValues?.generalInformation ?? {};
  const personalInformation = formValues?.personalInformation ?? {};
  const loadedGeneralInformation =
    occupantInfo?.residentInfo?.generalInformation ?? {};

  const loadedAdditionalPhones = getAdditionalContactEntries(
    occupantInfo?.contactInformation?.phones,
  );
  const loadedAdditionalEmails = getAdditionalContactEntries(
    occupantInfo?.contactInformation?.emails,
  );
  const additionalPhones = withAdditionalContactArray(
    buildAdditionalContactEntries(
      [generalInformation.secondaryPhone, generalInformation.tertiaryPhone],
      {
        valueKey: 'phoneNumber',
        typeKey: 'phoneType',
        defaultType: DEFAULT_PHONE_TYPE,
      },
    ),
    loadedAdditionalPhones,
    isEditHouseholdMemberDatesEnabled,
  );
  const additionalEmails = withAdditionalContactArray(
    buildAdditionalContactEntries(
      [generalInformation.secondaryEmail, generalInformation.tertiaryEmail],
      {
        valueKey: 'emailAddress',
        typeKey: 'emailType',
        defaultType: DEFAULT_EMAIL_TYPE,
      },
    ),
    loadedAdditionalEmails,
    isEditHouseholdMemberDatesEnabled,
  );

  return {
    // Applicant.id -- Applicants.js#updateApplicant reads it from the URL.
    id: occupantInfo?.id,
    isAdult: true,
    annualIncome: toClearableAnnualIncome(
      personalInformation.annualIncome,
      occupantInfo?.residentInfo?.personalInformation?.annualIncome,
    ),
    generalInformation: blankToUndefined({
      firstName: generalInformation.firstName,
      preferredName: nullIfBlank(generalInformation.preferredName),
      parentCompany: nullIfBlank(generalInformation.parentCompany),
      phoneNumber: generalInformation.phoneNumber,
      emailAddress: generalInformation.emailAddress,
      phoneType: generalInformation.phoneType,
      emailType: generalInformation.emailType,
      street: nullIfBlankLoaded(
        generalInformation.street,
        loadedGeneralInformation.street,
      ),
      street2: nullIfBlankLoaded(
        generalInformation.street2,
        loadedGeneralInformation.street2,
      ),
      city: nullIfBlankLoaded(
        generalInformation.city,
        loadedGeneralInformation.city,
      ),
      state: nullIfBlankLoaded(
        generalInformation.state,
        loadedGeneralInformation.state,
      ),
      zipCode: nullIfBlankLoaded(
        generalInformation.zipCode,
        loadedGeneralInformation.zipCode,
      ),
      additionalPhones,
      additionalEmails,
    }),
  };
};

// ApplicantMinors.js#updateMinor's URL id (cmd.applicantMinorId) must be the
// ApplicantMinor's own id — this.repository.update(minor.id, ...), which
// writes firstName/gender/race/etc., only runs `if (minor?.id)` i.e. only if
// that lookup succeeds. That's occupantInfo.residentInfo.id here (built by
// useHouseholdMembersQuery.helpers.js's parseResidentInfo), NOT the outer
// occupantInfo.id (which is the Resident row's id — a different record).
// residentId (occupantInfo.residentInfo.minorsResId, the actual Resident id)
// is threaded separately so useResidentMutation can send both without
// conflating them.
export const buildMinorSavePayload = (occupantInfo, formValues) => {
  const generalInformation = formValues?.generalInformation ?? {};
  const personalInformation = formValues?.personalInformation ?? {};
  const affordableInformation = formValues?.affordableInformation ?? {};

  return {
    id: occupantInfo?.residentInfo?.id,
    residentId: occupantInfo?.residentInfo?.minorsResId,
    applicantId: occupantInfo?.minorApplicantId,
    isAdult: false,
    generalInformation: blankToUndefined(
      withNullableDates({
        firstName: generalInformation.firstName,
        middleName: generalInformation.middleName,
        lastName: generalInformation.lastName,
        preferredName: generalInformation.preferredName,
        suffixId: nullIfBlank(generalInformation.suffixId),
        // Relation — lives on the minor's own Resident row (same shared
        // attribute the adult path writes to; see ApplicantMinors.js#
        // updateMinor), not ApplicantMinor. 'default' is the same
        // App/selectors.js getRelationshipOptions blank-placeholder value as
        // the adult Relation field uses.
        relationshipId: withoutDefaultRelationshipId(
          generalInformation.relationshipId,
        ),
        startDate: generalInformation.startDate,
        endDate: generalInformation.endDate,
      }),
    ),
    personalInformation: blankToUndefined({
      dateOfBirth: personalInformation.dateOfBirth,
      socialSecurityNumber: personalInformation.socialSecurityNumber,
      alienRegistrationNumber: personalInformation.alienRegistrationNumber,
    }),
    affordableInformation: blankToUndefined(
      withNullableAffordableFields({
        gender: affordableInformation.gender,
        jointCustody: affordableInformation.jointCustody,
        ...toRacePayload(affordableInformation),
        ethnicity: affordableInformation.ethnicity,
        studentType: affordableInformation.studentType,
        affordableRelationshipId:
          affordableInformation.affordableRelationshipId,
        disability: affordableInformation.disability,
      }),
    ),
    specialNeedsDesignationIds:
      affordableInformation.specialNeedsDesignationIds,
  };
};

// Applicants.js#updateApplicant's MiniProfileData (applicant.schema.js) only
// declares 6 generalInformation fields — no applicantTypeId/relationshipId/
// startDate/endDate, matching the ticket ("Allow editing only within General
// Information section" — and even within it, only these fields are actually
// persisted; Start/End Date are already disabled for Pending in
// useMiniProfileSections). Applicants.js updates Customer then
// ApplicantCustomer, in that order, for the ApplicantCustomer afterUpdate
// hook to re-sync Applicant.name from the just-written Customer name.
export const buildPendingApplicantSavePayload = (
  occupantInfo,
  formValues,
  { isEditHouseholdMemberDatesEnabled = false, isAffordable = false } = {},
) => {
  const generalInformation = formValues?.generalInformation ?? {};
  const affordableInformation = formValues?.affordableInformation ?? {};
  const personalInformation = formValues?.personalInformation ?? {};

  // Applicant columns, so top level. Key presence, not truthiness: an omitted key
  // leaves the column alone, an explicit null clears it (nullIfBlank maps '' and 'default').
  const withRelationField = (key, value) =>
    value === undefined ? {} : { [key]: nullIfBlank(value) };
  const relationFields = {
    ...withRelationField('applicantTypeId', generalInformation.applicantTypeId),
    ...withRelationField('relationshipId', generalInformation.relationshipId),
    // Affordable Relation only renders for Affordable; getDefaultValues seeds it
    // regardless, and sending it on Conventional would null out the stored value.
    ...(isAffordable
      ? withRelationField(
          'affordableRelationshipId',
          affordableInformation.affordableRelationshipId,
        )
      : {}),
    // Not gated on isAffordable: both of these render for Conventional too and
    // stay editable while Pending (STILL_EDITABLE_WHILE_READ_ONLY), so gating
    // them would discard the edit on Save.
    //
    // Array, not through nullIfBlank; an empty list removes the last designation.
    ...(affordableInformation.specialNeedsDesignationIds === undefined
      ? {}
      : {
          specialNeedsDesignationIds:
            affordableInformation.specialNeedsDesignationIds,
        }),
    // Conventional only, like the field itself: getDefaultValues seeds it from
    // the Applicant record either way, and an Affordable member never sees it,
    // so sending it there would rewrite a value nobody touched. Clearable --
    // `true` because a Pending Applicant's own row is never soft-deleted
    // (only a Prior/Current member's read can miss it), so blank here is
    // always a real, deliberate clear, never a load that silently failed.
    ...(isAffordable
      ? {}
      : {
          annualIncome: toClearableAnnualIncome(
            personalInformation.annualIncome,
            true,
          ),
        }),
  };

  // Same additionalPhones/additionalEmails shape as buildAdultSavePayload above,
  // nested under this endpoint's smaller GeneralInformation (applicant.schema.js).
  // Pending adults now surface `occupantInfo.contactInformation` via
  // useHouseholdPendingMembersQuery.helpers.js, so loadedAdditional* reflects
  // what was already saved (non-primary rows) just like the Current/Prior path.
  const loadedAdditionalPhones = getAdditionalContactEntries(
    occupantInfo?.contactInformation?.phones,
  );
  const loadedAdditionalEmails = getAdditionalContactEntries(
    occupantInfo?.contactInformation?.emails,
  );
  const additionalPhones = withAdditionalContactArray(
    buildAdditionalContactEntries(
      [generalInformation.secondaryPhone, generalInformation.tertiaryPhone],
      {
        valueKey: 'phoneNumber',
        typeKey: 'phoneType',
        defaultType: DEFAULT_PHONE_TYPE,
      },
    ),
    loadedAdditionalPhones,
    isEditHouseholdMemberDatesEnabled,
  );
  const additionalEmails = withAdditionalContactArray(
    buildAdditionalContactEntries(
      [generalInformation.secondaryEmail, generalInformation.tertiaryEmail],
      {
        valueKey: 'emailAddress',
        typeKey: 'emailType',
        defaultType: DEFAULT_EMAIL_TYPE,
      },
    ),
    loadedAdditionalEmails,
    isEditHouseholdMemberDatesEnabled,
  );

  return {
    // Applicant.id — Applicants.js#updateApplicant reads this from the URL;
    // for a Pending Applicant, occupantInfo.id IS the Applicant's own id
    // (buildHouseholdPendingMembersQuery.helpers.js's buildHouseholdPendingMembers).
    id: occupantInfo?.id,
    isAdult: true,
    ...relationFields,
    generalInformation: blankToUndefined({
      firstName: generalInformation.firstName,
      middleName: generalInformation.middleName,
      lastName: generalInformation.lastName,
      preferredName: generalInformation.preferredName,
      // Applicants.js#updateApplicant already guards this with
      // `!== undefined` (an explicit null is written, an omitted key isn't)
      // — needs nullIfBlank here too so clearing it actually sends that null
      // instead of getting stripped by blankToUndefined first.
      suffixId: nullIfBlank(generalInformation.suffixId),
      phoneNumber: generalInformation.phoneNumber,
      emailAddress: generalInformation.emailAddress,
      // Deliberately NOT defaulted to DEFAULT_PHONE_TYPE/DEFAULT_EMAIL_TYPE
      // here, unlike buildAdultSavePayload's primary -- occupantInfo.
      // contactInformation isn't populated for Pending Applicants by the
      // read path today, so getDefaultValues can't pre-fill this from an
      // existing DB row the way it does for Current/Prior. Blank here
      // could just mean "the read path didn't surface it," not "there's
      // truly no type yet" -- forcing a default would silently overwrite
      // an already-saved type on every untouched save. Sending it blank
      // (omitted, via blankToUndefined) lets the backend's own fallback
      // (Applicants.js -> buildPhoneArrayForSave/buildEmailArrayForSave)
      // preserve whatever's already there instead.
      phoneType: generalInformation.phoneType,
      emailType: generalInformation.emailType,
      additionalPhones,
      additionalEmails,
    }),
  };
};

// A Pending minor's own General/Personal/Affordable data lives on
// ApplicantMinor, not ApplicantCustomer/Customer -- Applicants.js#
// updateApplicant's ApplicantCustomer lookup would come back empty for one,
// silently dropping every field. Routed to ApplicantMinors.js#updateMinor
// instead (PUT .../applicantMinor/{id}/update, the same route a Current/Prior
// minor's buildMinorSavePayload above uses), addressed by the ApplicantMinor's
// own id -- no residentId/relationshipId/startDate/endDate, all three of
// which updateMinor only writes once a Resident row exists (see its own
// comment: "only reachable once this minor has an associated resident").
export const buildPendingApplicantMinorSavePayload = (
  occupantInfo,
  formValues,
) => {
  const generalInformation = formValues?.generalInformation ?? {};
  const personalInformation = formValues?.personalInformation ?? {};
  const affordableInformation = formValues?.affordableInformation ?? {};

  return {
    // The ApplicantMinor id, not the Applicant id the row is keyed on.
    id: occupantInfo?.applicantMinorId ?? occupantInfo?.id,
    isAdult: false,
    generalInformation: blankToUndefined({
      firstName: generalInformation.firstName,
      middleName: generalInformation.middleName,
      lastName: generalInformation.lastName,
      preferredName: generalInformation.preferredName,
      suffixId: nullIfBlank(generalInformation.suffixId),
      // Applicant column; a Pending minor has no Resident row to carry it.
      relationshipId: withoutDefaultRelationshipId(
        generalInformation.relationshipId,
      ),
    }),
    personalInformation: blankToUndefined({
      dateOfBirth: personalInformation.dateOfBirth,
      socialSecurityNumber: personalInformation.socialSecurityNumber,
      alienRegistrationNumber: personalInformation.alienRegistrationNumber,
    }),
    affordableInformation: blankToUndefined(
      withNullableAffordableFields({
        gender: affordableInformation.gender,
        jointCustody: affordableInformation.jointCustody,
        ...toRacePayload(affordableInformation),
        ethnicity: affordableInformation.ethnicity,
        studentType: affordableInformation.studentType,
        affordableRelationshipId:
          affordableInformation.affordableRelationshipId,
        disability: affordableInformation.disability,
      }),
    ),
    specialNeedsDesignationIds:
      affordableInformation.specialNeedsDesignationIds,
  };
};

// D5 -- the applicant-stage Add flow MUST create through
// PUT .../applications/{applicationId}/applicants (command updateApplicants),
// not editResidentHousehold's endpoint: the completed-move-in-certification
// auto-reopen and the Affordable-Relation validation both live inside that
// one command handler only. Shaped to its ApplicationChangeApplicants Joi
// schema (application.schema.js) directly -- an item array under `adults`/
// `minors`, the new member plus (adults only, see below) the rest of the
// household.
// All three keys (`adults`/`minors`/`pets`) must still be present, even as
// `[]` -- updateApplicants destructures all three off `modify` and calls
// `.forEach`/`.filter` on each with no guard, so an entirely OMITTED key
// (not just an empty one) throws a 400 before anything is saved.
//
// Unlike minors/pets, `adults` can NOT be just the one new/changed item:
// Applications.js#validateApplicationCreate validates the whole `adults`
// array unconditionally on every updateApplicants call, including "exactly
// one adult marked isProspect" and "at least one financially responsible
// adult" -- rules that only make sense read against the FULL household.
// Sending only the new adult fails both (0 candidates); sending `adults: []`
// fails the same way. updateApplicants also runs updateAdultApplicant for
// every present, non-deleted adult with an id (Applications.js), so each
// existing adult must be reshaped faithfully (same id/type/relationship/
// affordableRelationship/isProspect it already has), not invented -- an
// inaccurate resend would silently overwrite real data instead of just
// failing validation. Mirrors EditApplicationHousehold/index.js's
// generateInitialAdults + its own adult mapping, the same endpoint's own
// proven-correct payload for an unchanged existing adult.
const mapExistingAdultForApplicantPayload = (applicant) => {
  const customer = applicant?.applicantCustomer?.customer ?? {};
  const { email, phone } = getMainEmailAndPhone(
    customer.contact?.emails,
    customer.contact?.phones,
    customer,
  );
  return {
    id: applicant.id,
    firstName: customer.firstName,
    middleName: customer.middleName,
    lastName: customer.lastName,
    preferredName: customer.preferredName,
    suffixId: customer.suffixId ?? null,
    phone,
    email,
    type: applicant.applicantTypeId,
    relationship: applicant.relationshipId,
    affordableRelationship: applicant.affordableRelationshipId,
    isProspect: Boolean(applicant?.applicantCustomer?.isProspect),
    deleted: false,
  };
};

// Minors/pets don't need this treatment -- validateApplicationCreate's rules
// for them (max count, and Affordable Relation when Affordable) only ever
// look at whatever is actually present in the minors/pets arrays, so a call
// that isn't touching either legitimately sends them as `[]`/one new item,
// leaving the rest of the household alone (the same convention the legacy
// Edit Household form relies on for a same-length household with no other
// change). Only `adults` carries a household-wide rule.
// Exported so Household/ChangeHeadOfHousehold's applicant-path payload
// builder can resend the same existing-adults half instead of duplicating
// this mapping a third time.
export const getExistingApplicantAdults = (applicants = []) =>
  (applicants ?? [])
    .filter((applicant) => applicant?.applicantCustomer != null)
    .map(mapExistingAdultForApplicantPayload);

export const buildCreateApplicantAdultPayload = (
  formValues,
  { isEditHouseholdMemberDatesEnabled = false, applicants = [] } = {},
) => {
  const generalInformation = formValues?.generalInformation ?? {};
  const affordableInformation = formValues?.affordableInformation ?? {};
  const personalInformation = formValues?.personalInformation ?? {};

  // Additional (Secondary/Tertiary) entries only -- ApplicationChangeApplicants'
  // `phones`/`emails` arrays are supplementary to the `phone`/`email` scalars
  // below, unlike buildCreateAdultPayload's legacy-endpoint contract, which
  // needs the primary duplicated into the array too.
  const additionalPhones = isEditHouseholdMemberDatesEnabled
    ? buildAdditionalContactEntries(
        [generalInformation.secondaryPhone, generalInformation.tertiaryPhone],
        {
          valueKey: 'phoneNumber',
          typeKey: 'phoneType',
          defaultType: DEFAULT_PHONE_TYPE,
        },
      )
    : [];
  const additionalEmails = isEditHouseholdMemberDatesEnabled
    ? buildAdditionalContactEntries(
        [generalInformation.secondaryEmail, generalInformation.tertiaryEmail],
        {
          valueKey: 'emailAddress',
          typeKey: 'emailType',
          defaultType: DEFAULT_EMAIL_TYPE,
        },
      )
    : [];

  return {
    adults: [
      ...getExistingApplicantAdults(applicants),
      {
        type: generalInformation.applicantTypeId || null,
        firstName: generalInformation.firstName,
        middleName: generalInformation.middleName || null,
        lastName: generalInformation.lastName,
        suffixId: nullIfBlank(generalInformation.suffixId) || null,
        preferredName: generalInformation.preferredName || null,
        relationship: withoutDefaultRelationshipId(
          generalInformation.relationshipId,
        ),
        // Only ever populated while Adding at an Affordable household, same
        // as buildCreateAdultPayload -- promoted into General for exactly
        // this reason (useMiniProfileSections.js's isAdding && isAffordable
        // block). Enforced server-side too (updateApplicants throws if
        // missing for an Affordable household).
        affordableRelationship:
          affordableInformation.affordableRelationshipId === 'default'
            ? null
            : affordableInformation.affordableRelationshipId || null,
        phone: generalInformation.phoneNumber || null,
        email: generalInformation.emailAddress || null,
        // Omitted when nothing was chosen, so legacy Edit Household payloads are unchanged.
        ...(affordableInformation.specialNeedsDesignationIds?.length
          ? {
              specialNeedsDesignationIds:
                affordableInformation.specialNeedsDesignationIds,
            }
          : {}),
        // Conventional only, so blank on an Affordable Add -- omitted rather
        // than null, which application.schema.js would accept and store.
        ...(toAnnualIncome(personalInformation.annualIncome) === undefined
          ? {}
          : { annualIncome: toAnnualIncome(personalInformation.annualIncome) }),
        ...(additionalPhones.length > 0 ? { phones: additionalPhones } : {}),
        ...(additionalEmails.length > 0 ? { emails: additionalEmails } : {}),
        // Never isProspect -- a brand-new member can't be the Head of
        // Household. Exactly one EXISTING adult (from
        // getExistingApplicantAdults above) carries that flag as true,
        // which is what satisfies validateApplicationCreate's "exactly one
        // adult applicant must be marked as prospect" rule.
        deleted: false,
      },
    ],
    // updateApplicants destructures `{ adults, minors, pets }` off `modify`
    // and calls `.forEach`/`.filter` on all three with no guard -- an omitted
    // key (vs. an empty array) throws before the new adult is ever saved.
    minors: [],
    pets: [],
  };
};

// Same D5 route as the adult create above, `minors` instead of `adults`.
// Deliberately as limited as the legacy Edit Household form's own minor
// create through this same endpoint (Applications.js#
// createApplicantsForApplication only ever writes firstName/middleName/
// lastName/preferredName/suffixId/age/dateOfBirth for a new minor) -- ssn/
// race/ethnicity/gender/studentType/jointCustody/disability/specialNeeds
// DesignationIds are NOT persisted on create through this route today, a
// pre-existing limitation this card inherits rather than introduces (see
// ASSUMPTION 5). They can still be filled in on a later edit, once the minor
// has its own ApplicantMinor row to update via
// buildPendingApplicantMinorSavePayload above.
export const buildCreateApplicantMinorPayload = (
  formValues,
  { applicants = [] } = {},
) => {
  const generalInformation = formValues?.generalInformation ?? {};
  const personalInformation = formValues?.personalInformation ?? {};
  const affordableInformation = formValues?.affordableInformation ?? {};

  return {
    minors: [
      {
        firstName: generalInformation.firstName,
        middleName: generalInformation.middleName || null,
        lastName: generalInformation.lastName,
        suffixId: nullIfBlank(generalInformation.suffixId) || null,
        preferredName: generalInformation.preferredName || null,
        relationship: withoutDefaultRelationshipId(
          generalInformation.relationshipId,
        ),
        affordableRelationship:
          affordableInformation.affordableRelationshipId === 'default'
            ? null
            : affordableInformation.affordableRelationshipId || null,
        birthday: personalInformation.dateOfBirth || null,
        // Conventional minors only (the affordable Add keeps both disabled).
        // Omitted when blank so an Add without either still validates
        // against a backend without the two schema keys.
        ...(personalInformation.socialSecurityNumber
          ? { socialSecurityNumber: personalInformation.socialSecurityNumber }
          : {}),
        ...(personalInformation.alienRegistrationNumber?.trim()
          ? {
              alienRegistrationNumber:
                personalInformation.alienRegistrationNumber.trim(),
            }
          : {}),
        ...(affordableInformation.specialNeedsDesignationIds?.length
          ? {
              specialNeedsDesignationIds:
                affordableInformation.specialNeedsDesignationIds,
            }
          : {}),
        deleted: false,
      },
    ],
    // Same updateApplicants contract as buildCreateApplicantAdultPayload
    // above -- pets must be present (even empty) or the unconditional
    // `.forEach`/`.filter` on it throws before the new minor is saved.
    // `adults`, unlike pets, can't just be `[]` here either -- see
    // getExistingApplicantAdults above: validateApplicationCreate validates
    // the whole adults array (isProspect, financially-responsible) on every
    // updateApplicants call, this one included, even though it's only
    // adding a minor.
    adults: getExistingApplicantAdults(applicants),
    pets: [],
  };
};

// Same D5 route as the two creates above -- there is no separate delete
// route for an applicant-stage member (ENG-30354's is resident-scoped and
// 404s pre-move-in, by that ticket's own design). An adult delete flips
// `deleted: true` on the target's own entry inside the SAME existing-adults
// array getExistingApplicantAdults produces, rather than appending a new
// item: validateApplicationCreate still needs every surviving adult's
// isProspect/phone in the request. A minor delete only needs its own id +
// deleted:true -- the ApplicationChangeApplicants schema explicitly allows a
// blank firstName/lastName once deleted:true (application.schema.js), and
// minors carry no household-wide rule to preserve (see
// getExistingApplicantAdults above).
export const buildDeleteApplicantPayload = (
  member,
  { applicants = [] } = {},
) => {
  const existingAdults = getExistingApplicantAdults(applicants);
  if (member?.type === 'adult') {
    return {
      adults: existingAdults.map((adult) =>
        adult.id === member.applicantId ? { ...adult, deleted: true } : adult,
      ),
      minors: [],
      pets: [],
    };
  }
  return {
    adults: existingAdults,
    minors: [{ id: member?.applicantId, deleted: true }],
    pets: [],
  };
};

// ENG-30200 AC#3/#4 -- Add mode (occupantInfo has no id yet). Unlike every
// update payload above, there is no existing record to "leave unchanged",
// so an untouched/blank/placeholder field means "explicitly no value" here,
// not "don't touch this column" -- the opposite convention from
// blankToUndefined. Mirrors the legacy EditResidentHousehold saga's
// removeDefaults (sagas.js:121-129, D-fact #1/CONTEXT.md): 'default'/''/
// undefined all become an explicit null, and the key is always included
// (never omitted), matching how the backend's create handlers
// (addAdultApplicant/addMinorResident) read these columns.
const removeDefaultsToNull = (data) =>
  Object.keys(data).reduce((acc, key) => {
    const value = data[key];
    acc[key] = [undefined, null, '', 'default'].includes(value) ? null : value;
    return acc;
  }, {});

// Backend contract (PATCH .../residents/{residentId}/household, single-item
// array -- D4/CONTEXT.md): the SAME existing endpoint the legacy Edit
// Household flow uses, not a new one. Sending `id: undefined` here is
// equivalent to omitting the key entirely once JSON.stringify drops it --
// either way handleAdultResidentEdit's `isNil(payload.id)` check (backend)
// is what routes this to addAdultApplicant (Residents.js:2894-2990) instead
// of the update path.
export const buildCreateAdultPayload = (
  formValues,
  { isEditHouseholdMemberDatesEnabled = false } = {},
) => {
  const generalInformation = formValues?.generalInformation ?? {};
  const affordableInformation = formValues?.affordableInformation ?? {};
  const personalInformation = formValues?.personalInformation ?? {};

  // ENG-30290 -- see buildCreateAdultContactInformation above for the shape
  // (full array including the primary, explicit isPrimary, no id). Kept
  // outside removeDefaultsToNull deliberately: that helper always emits
  // every key it's given (turning an omitted/blank value into an explicit
  // null), which would send `contactInformation: null` on every create
  // instead of omitting the key -- the same "merge in separately" idiom
  // buildCreateMinorPayload below already uses for its own nested
  // disability/specialNeedsDesignationIds objects.
  const contactInformation = buildCreateAdultContactInformation(
    generalInformation,
    isEditHouseholdMemberDatesEnabled,
  );

  return [
    {
      type: 'adult',
      data: {
        ...removeDefaultsToNull({
          // The Applicant's own applicant-TYPE (e.g. Live-in Aide), NOT the
          // adult/minor discriminator above -- matches the legacy saga's
          // loadedAdultValues.type = la.applicant.applicantTypeId
          // (EditResidentHousehold/sagas.js).
          type: generalInformation.applicantTypeId,
          firstName: generalInformation.firstName,
          lastName: generalInformation.lastName,
          middleName: generalInformation.middleName,
          preferredName: generalInformation.preferredName,
          // Adults use `suffix`, NOT `suffixId` -- confirmed field-name
          // asymmetry vs. minors (D-fact #2/CONTEXT.md); the Mini-Profile's
          // own RHF field is still named suffixId (useMiniProfileSections.js)
          // since that's this form's internal name, not the wire name.
          suffix: generalInformation.suffixId,
          relationshipId: generalInformation.relationshipId,
          // Only ever populated while Adding at an Affordable household --
          // promoted into the General section for exactly this reason (see
          // useMiniProfileSections.js's isAdding && isAffordable block).
          // Enforced server-side too (Residents.js#addAdultApplicant throws
          // if missing for an Affordable household).
          affordableRelationshipId:
            affordableInformation.affordableRelationshipId,
          emailAddress: generalInformation.emailAddress,
          phoneNumber: generalInformation.phoneNumber,
        }),
        // Outside removeDefaultsToNull: an unchosen list must be omitted, not sent as null.
        ...(affordableInformation.specialNeedsDesignationIds?.length
          ? {
              specialNeedsDesignationIds:
                affordableInformation.specialNeedsDesignationIds,
            }
          : {}),
        // Same reason: blank must be omitted, and it always is on an
        // Affordable Add, where the field never renders.
        ...(toAnnualIncome(personalInformation.annualIncome) === undefined
          ? {}
          : { annualIncome: toAnnualIncome(personalInformation.annualIncome) }),
        // Carries the primary's Phone Type/Email Type (design gap,
        // ENG-30373, unconditional) AND Secondary/Tertiary Phone and Email
        // (ENG-30290, gated on the `edit-household-member-dates` flag, D2)
        // -- see buildCreateAdultContactInformation. Omitted entirely (not
        // `null`) when there's nothing to say for either.
        ...(contactInformation ? { contactInformation } : {}),
      },
    },
  ];
};

// Deliberately NO `id` key at all here (D-fact #1/CONTEXT.md) -- matches
// the legacy saga's newMinors shape exactly (`{ type: 'minor', data: ... }`,
// no id property whatsoever, vs. the adult branch's explicit `id:
// undefined`). Functionally identical once JSON.stringify drops undefined
// keys either way, but kept literal here for parity with the documented
// contract.
export const buildCreateMinorPayload = (formValues) => {
  const generalInformation = formValues?.generalInformation ?? {};
  const personalInformation = formValues?.personalInformation ?? {};
  const affordableInformation = formValues?.affordableInformation ?? {};

  return [
    {
      type: 'minor',
      data: {
        // Merged in rather than run through removeDefaultsToNull: one is an
        // object and the other an array, and neither is a column on the minor
        // row — the API stores the disability in its own table and the
        // designations on the applicant.
        //
        // Both keys are omitted entirely when the user left them alone.
        // blankToUndefined is what collapses an all-blank disability to
        // undefined: sending the `{}` it would otherwise be reads to the API
        // as "the user set this", and it then fails trying to write a row of
        // empty strings ("Error with updating Applicant Minor Disability").
        // Same reasoning as the update payloads above.
        ...(blankToUndefined(affordableInformation.disability)
          ? { disability: blankToUndefined(affordableInformation.disability) }
          : {}),
        ...(affordableInformation.specialNeedsDesignationIds?.length
          ? {
              specialNeedsDesignationIds:
                affordableInformation.specialNeedsDesignationIds,
            }
          : {}),
        ...removeDefaultsToNull({
          firstName: generalInformation.firstName,
          lastName: generalInformation.lastName,
          middleName: generalInformation.middleName,
          preferredName: generalInformation.preferredName,
          suffixId: generalInformation.suffixId,
          relationshipId: generalInformation.relationshipId,
          affordableRelationshipId:
            affordableInformation.affordableRelationshipId,
          // The form field is personalInformation.dateOfBirth, but the API
          // reads `birthday` here — matching the legacy form's own name for
          // this same value (EditResidentHousehold/Form/MinorFields.js).
          birthday: personalInformation.dateOfBirth,
          // A minor's sections are all editable while it's being created, so
          // whatever the user filled in has to be saved by this one request —
          // the Mini-Profile closes afterwards, and anything left out would be
          // lost without a trace. Every key below is a column on ApplicantMinor,
          // which the API creates straight from this object.
          socialSecurityNumber: personalInformation.socialSecurityNumber,
          alienRegistrationNumber:
            personalInformation.alienRegistrationNumber ||
            personalInformation.alienRegistration,
          gender: affordableInformation.gender,
          ...toRacePayload(affordableInformation),
          ethnicity: affordableInformation.ethnicity,
          studentType: affordableInformation.studentType,
          jointCustody: affordableInformation.jointCustody,
        }),
      },
    },
  ];
};

// `options.isEditHouseholdMemberDatesEnabled` (ENG-30290/D2) is consumed by
// every adult path, update AND create -- buildCreateAdultPayload assembles
// its own `data.contactInformation` shape (full array + isPrimary, the
// legacy create endpoint's contract, different from the update paths'
// additionalPhones/additionalEmails -- see buildCreateAdultContactInformation
// above). Minors never get the new fields, so buildCreateMinorPayload/
// buildMinorSavePayload don't need the flag at all.
//
// `options.isApplicantSubject` (this card) routes Add through updateApplicants
// instead of editResidentHousehold (D5) -- MiniProfileBridge sets it from the
// `subject` prop it was opened with, the one place both this function and the
// mutation it feeds need to agree on which endpoint owns the create.
//
// `options.applicants` (raw application.applicants, Redux) is what
// buildCreateApplicantAdultPayload/buildCreateApplicantMinorPayload reshape
// into the rest of the `adults` array -- see getExistingApplicantAdults
// above for why that's required, not optional, on the applicant-subject
// create path.
export const buildMiniProfileSavePayload = (
  occupantInfo,
  formValues,
  options = {},
) => {
  // ENG-30200 AC#3/#4 -- Add mode, checked first: occupantInfo has no id at
  // all yet (as opposed to a Pending Applicant, which has occupantInfo.id
  // set to the Applicant's own id -- see buildPendingApplicantSavePayload's
  // comment). occupantInfo.type is always already set at this point
  // ('adult'|'minor' -- ResidentProfile/index.js's newOccupantInfo, set from
  // the AddHouseholdMemberModal chooser).
  if (!occupantInfo?.id) {
    if (options.isApplicantSubject) {
      return occupantInfo?.type === 'adult'
        ? buildCreateApplicantAdultPayload(formValues, options)
        : buildCreateApplicantMinorPayload(formValues, options);
    }
    return occupantInfo?.type === 'adult'
      ? buildCreateAdultPayload(formValues, options)
      : buildCreateMinorPayload(formValues);
  }
  if (occupantInfo?.status === 'Pending') {
    // Every applicant-subject member is Pending, so the commercial applicant has to be
    // caught here, ahead of the residential Pending builder.
    if (options.isCommercial && occupantInfo?.type === 'adult') {
      return buildCommercialApplicantSavePayload(
        occupantInfo,
        formValues,
        options,
      );
    }
    // A Pending minor only ever exists on the applicant subject (see
    // useMiniProfileSubject.helpers.js) -- the resident side never produces
    // one (ENG-30200: minors don't have a pending state there), so this
    // branch is unreachable for occupantInfo.type === 'minor' today outside
    // that context.
    return occupantInfo?.type === 'adult'
      ? buildPendingApplicantSavePayload(occupantInfo, formValues, options)
      : buildPendingApplicantMinorSavePayload(occupantInfo, formValues);
  }
  // Current/Prior commercial tenant, gated on type === 'adult': a commercial
  // minor still has only a Resident row (no ResidentCustomer), and
  // buildCommercialSavePayload's hard-coded isAdult: true would 500 it.
  if (options.isCommercial && occupantInfo?.type === 'adult') {
    return buildCommercialSavePayload(occupantInfo, formValues, options);
  }
  return occupantInfo?.type === 'adult'
    ? buildAdultSavePayload(occupantInfo, formValues, options)
    : buildMinorSavePayload(occupantInfo, formValues);
};

export default buildMiniProfileSavePayload;
